import React, { useEffect, useState } from 'react';
import {
    Breadcrumb, Layout, Row, Col, Form, Button, Select,
    Table, Card, Space, Input, Tooltip,
    Spin, Popconfirm
} from 'antd';

import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MAINAPIURL, APPBACKGROUND_COLOR } from '../appsettings.js';
import axios from 'axios';
import { AlertError, AlertSuccess } from '../usableclasses.js';

const { Content } = Layout;
const { Option } = Select;

function StocksTransferForm() {
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");
    const UPDATED_BY = localStorage.getItem("USERID");
    const { transferId } = useParams();
    const navigator = useNavigate();

    const [modalForm] = Form.useForm()
    const [transferHeaderInfo, setTransferHeaderInfo] = useState({});
    const [transferItems, setTransferItems] = useState([]);
    const [stockCreateId, setStockCreateId] = useState(null);
    const [productsDropdown, setProductsDropdown] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (transferId) {
            fetchTransferDetails();
        }
    }, [transferId]);

    const fetchTransferDetails = async () => {
        try {

            const headers = { SystemAccessToken: TOKEN };
            const headerRes = await axios.get(`${ApiURL}/stocktransfer/transferheaderdetails/${transferId}`, { headers });

            const payload = {
                fromStockId: Number(headerRes?.data?.fromStockId),
                toStockId: Number(headerRes?.data?.toStockId),
                createdBy: UPDATED_BY
            };

            setIsLoading(true)
            setStockCreateId(transferId);

            const [itemsRes, productsRes] = await Promise.all([
                axios.get(`${ApiURL}/stocktransfer/transferitemsdetails/${transferId}`, { headers }),
                axios.post(`${ApiURL}/stocktransfer/getproductsfordropdown`, payload, { headers })
            ]);

            setTransferHeaderInfo(headerRes.data);
            setTransferItems(itemsRes.data);
            setProductsDropdown(productsRes.data);
        } catch (error) {
            AlertError("Failed to create stock transfer.");
        } finally {
            setIsLoading(false)
        }
    };


    const setProduct = (id) => {
        const product = productsDropdown.find((pro) => pro.productId === id);
        setSelectedProduct(product || null);
    };

    const addItem = async (values) => {
        const transferQty = Number(values.transferQty);
        setIsLoading(true)
        if (!selectedProduct) {
            AlertError("Please select a product.");
            return;
        }

        if (!transferQty || transferQty <= 0) {
            AlertError("Transfer quantity must be greater than zero.");
            return;
        }

        if (transferQty > selectedProduct.fromStockQty) {
            AlertError("Transfer quantity exceeds available stock.");
            return;
        }

        const payload = {
            fromStockProductId: selectedProduct.fromStockId,
            toStockProductId: selectedProduct.toStockId,
            transId: stockCreateId,
            productId: selectedProduct.productId,
            transferQty: transferQty,
            addedBy: UPDATED_BY
        };

        const headers = { SystemAccessToken: TOKEN };

        try {
            await axios.post(`${ApiURL}/stocktransfer/additem`, payload, { headers });
            await refreshItems();
            modalForm.resetFields();
            setSelectedProduct(null);
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to complete transfer.";

            AlertError(errorMessage);
        } finally {
            setIsLoading(false)
        }
    };

    const deleteItem = async (productId) => {
        const headers = { SystemAccessToken: TOKEN };
        const payload = {
            transId: stockCreateId,
            productId: productId,
            deletedBy: UPDATED_BY
        };

        try {
            await axios.post(`${ApiURL}/stocktransfer/deleteitem`, payload, { headers });
            await refreshItems();
            AlertSuccess("Item deleted.");
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to complete transfer.";

            AlertError(errorMessage);
        }
    };

    const completeTransfer = async () => {
        setIsLoading(true)
        const headers = { SystemAccessToken: TOKEN };
        const payload = {
            transferId: stockCreateId,
            status: "COMPLETE",
            updatedBy: UPDATED_BY
        };

        try {
            await axios.post(`${ApiURL}/stocktransfer/completetransfer`, payload, { headers });
            AlertSuccess("Transfer completed.");
            reset();
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to complete transfer.";

            AlertError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const reset = () => {
        modalForm.resetFields();
        setSelectedProduct(null);
        setTransferHeaderInfo({});
        setTransferItems([]);
        setProductsDropdown([]);
        setStockCreateId(null);

        navigator("/stocks/transfer")
    };

    const refreshItems = async () => {
        const headers = { SystemAccessToken: TOKEN };
        try {
            const itemsRes = await axios.get(`${ApiURL}/stocktransfer/transferitemsdetails/${stockCreateId}`, { headers });
            setTransferItems(itemsRes.data);
        } catch (error) {
            AlertError("Failed to fetch transfer items.");
        }
    }


    const transferItemColumn = [
        { title: 'Code', dataIndex: 'productSku' },
        { title: 'Name', dataIndex: 'productName' },
        { title: 'Size', dataIndex: 'productSize' },
        { title: 'UOM', dataIndex: 'productUOM' },
        { title: 'Transfer Qty', dataIndex: 'transferQty' },
        {
            title: 'Action',
            render: (_, record) => (
                <Space>
                    <Popconfirm
                        title="Are you sure to delete this transferee item?"
                        onConfirm={() => deleteItem(record.productId)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Tooltip title="Delete">
                            <Button

                                type="default"
                                style={{
                                    borderColor: '#ff4d4f', // Red border
                                    color: '#ff4d4f',
                                }}
                                size="small"
                                disabled={isLoading}
                            >
                                Delete
                            </Button>
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        }
    ];

    return (
        <Spin spinning={isLoading} tip="Processing...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
                <Headerdiv />
                <Content style={{ padding: '20px' }}>
                    <Breadcrumb style={{ margin: '16px 0' }}>
                        <Breadcrumb.Item><Link to={"/stocks/management"}>Stocks</Link></Breadcrumb.Item>
                        <Breadcrumb.Item><Link to={"/stocks/transfer"}>Stocks Transfer</Link></Breadcrumb.Item>
                        <Breadcrumb.Item>{stockCreateId}</Breadcrumb.Item>
                    </Breadcrumb>
                    <Card style={{ marginBottom: 10 }}>
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <strong>Transfer Reference:</strong> {transferHeaderInfo.transReferance}
                            </Col>
                            <Col xs={24} md={6}><strong>From Stock:</strong><br />{transferHeaderInfo.fromStockName}</Col>
                            <Col xs={24} md={6}><strong>To Stock:</strong><br />{transferHeaderInfo.toStockName}</Col>
                            <Col xs={24} md={6}><strong>Created By:</strong><br />{transferHeaderInfo.createdUsername}</Col>
                            <Col xs={24} md={6}><strong>Created On:</strong><br />{transferHeaderInfo.createdDatetime}</Col>
                        </Row>
                    </Card>
                    <Card style={{ marginBottom: 10 }}>
                        <Form layout="vertical" form={modalForm} onFinish={addItem} style={{ marginTop: 24 }}>
                            <Row gutter={16}>
                                <Col md={16}>
                                    <Form.Item
                                        label="Select Product"
                                        name="productId"
                                        rules={[{ required: true, message: "Please select a product" }]}
                                    >
                                        <Select onSelect={setProduct} placeholder="Select a product">
                                            {productsDropdown.map(product => (
                                                <Option key={product.productId} value={product.productId}>
                                                    {`${product.productCode} - ${product.productName}`}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col md={4}>
                                    <Form.Item label="Size">
                                        <Input value={selectedProduct?.productSize} readOnly />
                                    </Form.Item>
                                </Col>
                                <Col md={4}>
                                    <Form.Item label="UOM">
                                        <Input value={selectedProduct?.productUom} readOnly />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col md={6}>
                                    <Form.Item label="From Stock Qty">
                                        <Input value={selectedProduct?.fromStockQty} readOnly />
                                    </Form.Item>
                                </Col>
                                <Col md={6}>
                                    <Form.Item label="To Stock Qty">
                                        <Input value={selectedProduct?.toStockQty} readOnly />
                                    </Form.Item>
                                </Col>
                                <Col md={6}>
                                    <Form.Item
                                        label="Transfer Qty"
                                        name="transferQty"
                                        rules={[{ required: true, message: "Please enter a transfer quantity" }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col md={6}>
                                    <Button htmlType="submit" type="primary" style={{ marginTop: 30, width: "100%" }}>
                                        Add To List
                                    </Button>
                                </Col>
                            </Row>
                        </Form>

                    </Card>
                    <Card style={{ marginBottom: 10 }}>
                        <Table
                            columns={transferItemColumn}
                            dataSource={transferItems}
                            rowKey="productId"
                            style={{ marginTop: 20 }}
                        />
                    </Card>
                    <Row align="bottom" justify="end" gutter={16}>
                        <Col>
                            <Tooltip title="Save Draft">
                                <Button type="default" onClick={reset} disabled={isLoading}>
                                    Save as Draft
                                </Button>
                            </Tooltip>
                        </Col>
                        <Col>
                            <Popconfirm
                                title="Are you sure you want to complete this transfer?"
                                onConfirm={completeTransfer}
                                okText="Yes"
                                cancelText="No"
                                disabled={isLoading}
                            >
                                <Tooltip title="Complete Transfer">
                                    <Button type="primary" disabled={isLoading}>
                                        Complete & Finished
                                    </Button>
                                </Tooltip>
                            </Popconfirm>

                        </Col>
                    </Row>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
}

export default StocksTransferForm;
